/* eslint-disable sonarjs/cognitive-complexity */
/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
/* eslint-disable jsdoc/require-jsdoc */
import dayjs, { type Dayjs } from 'dayjs'

import { type TPostulanteInfo } from '@/containers/applications/table/config'
import {
  EFormEntityType,
  type TFormField,
  type TApplication,
  type TEntityInfo,
  EFormFieldTypes,
  type TEntitySection,
  EApplicationCreateMode,
  EApplicationStatus,
  EApplicationType,
} from '@/interfaces'

import { ConvocatoriasState } from '../convocatorias'
import { FormState } from '../form'
import { OfflineState } from '../offline-management'

import { ApplicationsState } from '.'
import { notification } from 'antd'
import { AuthState } from '../auth'

export function setFormInitialValues(
  formStructure: TEntityInfo[],
  applicationValuesInitial?: TApplication
): TApplication {
  const applicationValues: TApplication = {}

  formStructure.forEach((entity) => {
    const entityName = entity.name

    if (entityName) {
      if (entity.type === EFormEntityType.SIMPLE) {
        const initialEntityValues: TApplication = applicationValuesInitial
          ? (applicationValuesInitial[entityName] as TApplication)
          : {}

        applicationValues[entityName] = setSimpleEntityInitialValues(
          initialEntityValues,
          entity.sections
        )
      }

      if (entity.type === EFormEntityType.TABLE) {
        const initialEntityValues: TApplication[] = applicationValuesInitial
          ? (applicationValuesInitial[entityName] as TApplication[])
          : []

        applicationValues[entityName] = [...(initialEntityValues ?? [])]
      }
    }
  })

  return applicationValues
}

export function setSimpleEntityInitialValues(
  entityValues: TApplication,
  sections?: TEntitySection[]
) {
  const initialValues: TApplication = {}

  sections?.forEach((section) => {
    section.fields?.forEach((field) => {
      let fieldInitialValue = getFieldDefaultValue(field, entityValues)

      if (
        field.type === EFormFieldTypes.DATE &&
        typeof fieldInitialValue === 'string'
      ) {
        fieldInitialValue = dayjs(fieldInitialValue, 'YYYY-MM-DD')
      }

      if (
        field.type === EFormFieldTypes.DATE_TIME &&
        typeof fieldInitialValue === 'string'
      ) {
        fieldInitialValue = dayjs(fieldInitialValue, 'YYYY-MM-DD HH:mm:ss')
      }

      initialValues[field.name] = fieldInitialValue
    })
  })

  return initialValues
}

export function getFieldDefaultValue(
  field: TFormField,
  existingEntityValues?: TApplication
) {
  let existingData = null

  if (existingEntityValues) {
    existingData = existingEntityValues[field.name]
  }

  if (existingData !== undefined) {
    return existingData
  }

  if (field.default !== undefined) {
    return field.default
  }

  switch (field.type) {
    case EFormFieldTypes.BOOLEAN_SWITCH:
    case EFormFieldTypes.BOOLEAN:
      return false
    case EFormFieldTypes.TEXT:
    case EFormFieldTypes.VARCHAR:
    default:
      return null
  }
}

export function generateCustomId() {
  return [...Array(17)]
    .map(() => Math.floor(Math.random() * 16).toString(16))
    .join('')
}

export function filterApplications() {
  let applicationsFiltered = ApplicationsState.applications.value

  const currentConvocatoriaId = ConvocatoriasState.currentConvocatoria.value.id
  const currentConvocatoriaCity = ConvocatoriasState.currentCity.value
  const isOnline = OfflineState.isOnline.value

  if (!isOnline) {
    applicationsFiltered = applicationsFiltered.filter((item) =>
      OfflineState.storeApplications.value.includes(item.id as string)
    )
  }

  if (currentConvocatoriaId) {
    applicationsFiltered = applicationsFiltered.filter(
      (item) => item.convocatoriaId === currentConvocatoriaId
    )
  }

  if (currentConvocatoriaCity) {
    applicationsFiltered = applicationsFiltered.filter((item) =>
      ((item?.teamsIds ?? []) as string[]).includes(currentConvocatoriaCity)
    )
  }

  const filterStatus = ApplicationsState.filters.value.status
  if (filterStatus) {
    applicationsFiltered = applicationsFiltered.filter(
      (item) => filterStatus === item.estadoName
    )
  }

  const filterText = ApplicationsState.filters.value.text
  if (filterText) {
    applicationsFiltered = applicationsFiltered.filter((item) => {
      const postulante = item.CPostulante as TPostulanteInfo
      const postulanteFullName =
        `${postulante?.firstName || postulante?.name || ''} ${postulante?.lastName || ''}`
          .trim()
          .toLowerCase()

      return Boolean(
        (item.id as string)?.includes(filterText) ||
          postulanteFullName.includes(filterText.toLowerCase()) ||
          postulante?.documento?.includes(filterText)
      )
    })
  }

  const filterType = ApplicationsState.filters.value.type
  if (filterType) {
    applicationsFiltered = applicationsFiltered.filter(
      (item) => item.typeimprovement === filterType
    )
  }

  return applicationsFiltered
}

export function normalizeSynchedApp(application: TApplication) {
  application.toSync = false
  application.createMode = EApplicationCreateMode.ONLINE

  if (application.estadoName === EApplicationStatus.OFFLINE) {
    application.estadoName = EApplicationStatus.WITHOUT_START
  }

  return { ...application }
}

export function setCurrentEntityDataOnSave(newEntityData: TApplication): void {
  try {
    // Si la entidad está inhabilitada, forzar el estado a DISABLED
    const isInhabilitado = newEntityData.inhabilitado === true
    // Get previous application object for status fallback
    const previousApplication = ApplicationsState.currentApplication.peek() ?? {}
    let disabledStatus = isInhabilitado ? EApplicationStatus.DISABLED : (newEntityData.applicationstatus || previousApplication.applicationstatus)
    let disabledEstadoName = isInhabilitado ? EApplicationStatus.DISABLED : (newEntityData.estadoName || previousApplication.estadoName)

    // Fallback to default if still undefined
    if (!disabledStatus) {
      disabledStatus = EApplicationStatus.WITHOUT_START
    }
    if (!disabledEstadoName) {
      disabledEstadoName = EApplicationStatus.WITHOUT_START
    }

    // Only assign known TEntityInfo properties to FormState.currentEntity
    const { inhabilitado, detalleInhabilitacion, applicationstatus, estadoName, ...entityInfoFields } = newEntityData
    FormState.currentEntity.value = {
      ...FormState.currentEntity.peek(),
      ...entityInfoFields,
    }
    console.log('[DEBUG] setCurrentEntityDataOnSave - entidad actualizada:', FormState.currentEntity.value)

    const currentEntityName = FormState.currentEntity.peek()?.name
    const isOnline = OfflineState.isOnline.peek()

    let currentApplicationValues =
      ApplicationsState.currentApplication.peek() ?? {}

    if (currentEntityName) {
      const currentEntityValues =
        currentApplicationValues[currentEntityName] ??
        ({} satisfies TApplication)

      const currentEntityDateFields = FormState.currentEntityDateFields.peek()
      if (currentEntityDateFields.length > 0) {
        currentEntityDateFields.forEach((field) => {
          const fieldValue = (newEntityData[field.name] as Dayjs) ?? undefined

          if (fieldValue && fieldValue.format) {
            if (field.type === EFormFieldTypes.DATE) {
              newEntityData[field.name] = fieldValue.format('YYYY-MM-DD')
            }

            if (field.type === EFormFieldTypes.DATE_TIME) {
              newEntityData[field.name] = fieldValue.format(
                'YYYY-MM-DD HH:mm:ss'
              )
            }
          }
        })
      }

      currentApplicationValues = {
        ...currentApplicationValues,
        [currentEntityName]: {
          ...(currentEntityValues as TApplication),
          ...newEntityData,
        },
        toSync: !isOnline,
        // Forzar el estado global si está inhabilitado
        applicationstatus: disabledStatus,
        estadoName: disabledEstadoName,
        inhabilitado: newEntityData.inhabilitado,
        detalleInhabilitacion: newEntityData.detalleInhabilitacion,
      }
    }

    ApplicationsState.currentApplication.value = { ...currentApplicationValues }
    console.log('[DEBUG] setCurrentEntityDataOnSave - aplicación actualizada:', ApplicationsState.currentApplication.value)
  } catch (error) {
    console.error(error)
    notification.info({
      description:
        'La solicitud se ha actualizado correctamente pero ocurrió un error en el procesamiento de información posterior',
      message: 'Información actualizada',
      placement: 'top',
    })
  }
}

export function checkViewAccess() {
  const canReadApp = AuthState.applicationPermission.peek().read

  const totalEntitiesAccess = FormState.currentForm
    .peek()
    .filter((entity) => entity.permission?.read).length

  return Boolean(canReadApp && totalEntitiesAccess)
}
